import * as React from "react"

import { cn } from "@/lib/utils"

/**
 * Shared Input component.
 *
 * For `type="number"` we harden the field against two silent-corruption
 * bugs that caused a July 2026 negative-balance incident:
 *   1. **Scroll-wheel mutation** — hovering a number input and scrolling
 *      the page would decrement the value. We swallow wheel events by
 *      immediately blurring the field.
 *   2. **Negative-value injection** — the "-" key, the "e" key
 *      (scientific notation, e.g. `1e-5`), and pasted negatives all used
 *      to slip through. We block them at the keydown / paste layer.
 *   3. **Spinner arrows** — hidden globally via CSS in `index.css`.
 *
 * If a caller *needs* negatives (rare — e.g. an adjustment field), pass
 * `data-allow-negative` on the element and this guard is skipped.
 */
const Input = React.forwardRef(({ className, type, onWheel, onKeyDown, onPaste, ...props }, ref) => {
  const isNumber = type === "number";
  const allowNegative = props["data-allow-negative"] !== undefined && props["data-allow-negative"] !== false;

  const handleWheel = React.useCallback((e) => {
    if (isNumber) {
      // Blur so the wheel event stops updating the input's value.
      e.currentTarget.blur();
    }
    if (onWheel) onWheel(e);
  }, [isNumber, onWheel]);

  const handleKeyDown = React.useCallback((e) => {
    if (isNumber && !allowNegative) {
      // Block negative sign, scientific notation, and up/down arrow steps.
      if (["-", "e", "E", "+"].includes(e.key)) {
        e.preventDefault();
      }
      // Up / Down arrow keys also step number inputs — disable them too.
      if (e.key === "ArrowUp" || e.key === "ArrowDown") {
        e.preventDefault();
      }
    }
    if (onKeyDown) onKeyDown(e);
  }, [isNumber, allowNegative, onKeyDown]);

  const handlePaste = React.useCallback((e) => {
    if (isNumber && !allowNegative) {
      const pasted = (e.clipboardData || window.clipboardData).getData("text");
      if (/-/.test(pasted)) {
        e.preventDefault();
      }
    }
    if (onPaste) onPaste(e);
  }, [isNumber, allowNegative, onPaste]);

  return (
    <input
      type={type}
      // Enforce non-negative at the HTML layer as well, so browser-native
      // validation kicks in on form submit.
      min={isNumber && !allowNegative ? (props.min ?? 0) : props.min}
      onWheel={handleWheel}
      onKeyDown={handleKeyDown}
      onPaste={handlePaste}
      className={cn(
        "flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-base shadow-sm transition-colors file:border-0 file:bg-transparent file:text-sm file:font-medium file:text-foreground placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50 md:text-sm",
        className
      )}
      ref={ref}
      {...props} />
  );
})
Input.displayName = "Input"

export { Input }
